<template>
    <div class="qe-page">
        <v-breadcrumbs :items="breadcrumbsItems" class="pa-0 mb-2" large></v-breadcrumbs>

        <!-- 手機提醒：只看寬度，iPad 不會看到（spec §10.2） -->
        <v-alert v-if="showNarrowNotice" color="warning" text dense dismissible
            class="mb-3" @input="dismissNarrowNotice">
            <div class="font-weight-medium grey--text text--darken-3">
                {{ $t('unit.narrow_notice.title') }}
            </div>
            <div class="text-caption grey--text text--darken-2">
                {{ $t('unit.narrow_notice.body') }}
            </div>
        </v-alert>

        <v-overlay :value="isLoading" absolute opacity="0.1">
            <v-progress-circular indeterminate size="48" color="primary"></v-progress-circular>
        </v-overlay>

        <v-card v-if="loadFailed" outlined class="pa-6 text-center">
            <v-icon size="48" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <div class="mt-3 mb-4 grey--text text--darken-1">
                {{ $t('unit.quest.load_failed') }}
            </div>
            <v-btn color="primary" outlined class="normal-case mr-2" @click="load">
                {{ $t('unit.list.reload') }}
            </v-btn>
            <v-btn text class="normal-case" @click="backToList">{{ $t('unit.quest.back') }}</v-btn>
        </v-card>

        <template v-else-if="!isLoading">
            <!-- 標題列 -->
            <div class="qe-bar">
                <a class="qe-back" href="#" @click.prevent="backToList">
                    ← {{ unitName
                        ? $t('unit.quest.back_named', { name: unitName })
                        : $t('unit.quest.back') }}
                </a>
                <span class="qe-spacer"></span>
                <span v-if="isDirty" class="qe-unsaved">{{ $t('unit.quest.unsaved') }}</span>

                <!--
                    檢查徽章：點開浮層清單，每列可跳到該畫格。
                    ⚠ **沒有變更又完全沒問題時整顆隱藏**。綠色的「設定完整，可以儲存」
                    在剛打開頁面、什麼都還沒動的時候會被讀成「有東西待儲存」。
                    有問題（橘／紅）則不論有沒有變更都要顯示，那是老師需要知道的。
                -->
                <div v-if="checkRows.length || isDirty" v-click-outside="closeCheck"
                    class="chk-wrap ml-3">
                    <button type="button" class="chk"
                        :class="blockRows.length ? 'chk--block'
                            : (warnRows.length ? 'chk--warn' : 'chk--ok')"
                        @click="checkOpen = !checkOpen">
                        {{ badgeText }}
                    </button>
                    <div v-if="checkOpen" class="chk-pop">
                        <div class="chk-pop-head">{{ $t('unit.check.title') }}</div>
                        <div v-if="!checkRows.length" class="chk-row">
                            <span class="chk-mark chk-mark--ok">✓</span>
                            <span class="chk-label">{{ $t('unit.check.ok') }}</span>
                        </div>
                        <button v-for="(row, ri) in checkRows" :key="ri" type="button"
                            class="chk-row chk-row--btn" @click="jumpTo(row)">
                            <span class="chk-mark"
                                :class="row.level === 'block'
                                    ? 'chk-mark--block' : 'chk-mark--warn'">!</span>
                            <span class="chk-body">
                                <span class="chk-label">{{ row.label }}</span>
                                <span class="chk-reason"
                                    :class="{ 'chk-reason--block': row.level === 'block' }">
                                    {{ row.reason }}
                                </span>
                            </span>
                        </button>
                        <div v-if="blockRows.length" class="chk-pop-foot">
                            {{ $t('unit.check.blocked_hint') }}
                        </div>
                    </div>
                </div>

                <div class="qe-save ml-3">
                    <v-btn :color="canSave ? 'primary' : undefined" depressed class="normal-case"
                        :loading="isSaving" :disabled="!canSave" @click="save">
                        {{ saveLabel }}
                        <template #loader>
                            <v-progress-circular indeterminate size="18" width="2" class="mr-2" />
                            {{ $t('unit.quest.saving') }}
                        </template>
                    </v-btn>
                    <span v-if="blockRows.length && (isDirty || audioRetryRequested)"
                        class="qe-save-hint" role="status">
                        {{ $t('unit.quest.save_incomplete') }}
                    </span>
                </div>
            </div>

            <!-- 題目卡 -->
            <div class="card qe-question">
                <div class="two-col two-col--quest" data-rwd="two">
                    <div class="qe-col">
                        <div class="lbl">{{ $t('unit.quest.title_label') }}</div>
                        <!-- ⚠ 計數移到框內右側（2026-08-31 依設計圖）。
                             定位基準是這個 .field 而不是 .col——textarea 是
                             `resize: vertical`，錨在 .col 上時被拉高就會脫位。 -->
                        <div class="field">
                            <input class="in in--lg in--counted" type="text" v-model="questTitle"
                                :maxlength="QUEST_TITLE_MAX"
                                :placeholder="$t('unit.quest.title_ph')">
                            <span class="cnt cnt--inset">
                                {{ questTitle.length }} / {{ QUEST_TITLE_MAX }}
                            </span>
                        </div>
                    </div>
                    <div class="qe-col">
                        <div class="lbl">
                            {{ $t('unit.quest.description_label') }}
                            <span class="lbl-note">{{ $t('unit.quest.description_note') }}</span>
                        </div>
                        <div class="field">
                            <textarea class="in ta ta--tall ta--counted" v-model="questDescription"
                                :maxlength="DESC_MAX"
                                :placeholder="$t('unit.quest.description_ph')"></textarea>
                            <!-- ⚠ 底部的計數要有底色：textarea 的文字會捲到它下面，
                                 沒有底色就會疊在一起看不清。 -->
                            <span class="cnt cnt--inset cnt--bottom">
                                {{ questDescription.length }} / {{ DESC_MAX }}
                            </span>
                        </div>
                    </div>
                    <div class="qe-col">
                        <div class="lbl">{{ $t('unit.quest.thumb_label') }}</div>
                        <!-- ⚠ 兩條路，依「這一題有沒有 mode」分岔，不是功能開關。
                             沒有 mode 的舊題目送 cover_media_id 會被後端靜默丟掉
                             （規格 §9），所以它們必須留在既有的獨立上傳端點上。

                             ⚠ 有 mode 的分支**直接用 MediaSlot**（2026-08-31 依設計圖）：
                             設計圖裡封面欄與模式設定區的素材欄是同一個盒子，只差在
                             「未選 / 已選」兩種狀態。共用一個元件才不會兩邊各自漂移。
                             ⚠ 刻意**不提供貼網址**：外部圖片有版權疑慮（2026-08-12 決定）。 -->
                        <MediaSlot v-if="usesMediaLibrary" compact
                            :media="coverSlotMedia"
                            :placeholder="$t('unit.quest.thumb_default')"
                            :pick-label="$t('unit.quest.cover_pick')"
                            @pick="coverPickerOpen = true"
                            @clear="removeCover"
                            @edit="coverEditOpen = true" />

                        <div v-else class="thumb-row">
                            <div class="thumb-wrap">
                                <img class="thumb" :src="coverPreviewUrl" alt="">
                                <v-overlay :value="isUploadingThumb" absolute opacity="0.4">
                                    <v-progress-circular indeterminate size="22" width="2">
                                    </v-progress-circular>
                                </v-overlay>
                            </div>

                            <div class="thumb-side">
                                <button type="button" class="btn-outline"
                                    :disabled="isUploadingThumb" @click="pickThumb">
                                    {{ thumbnailUrl
                                        ? $t('unit.quest.thumb_replace')
                                        : $t('unit.quest.thumb_upload') }}
                                </button>
                                <button v-if="thumbnailUrl" type="button" class="btn-text-danger"
                                    :disabled="isUploadingThumb" @click="removeThumb">
                                    {{ $t('unit.quest.thumb_remove') }}
                                </button>
                                <div v-else class="cnt cnt--left">
                                    {{ $t('unit.quest.thumb_default') }}
                                </div>
                            </div>
                        </div>

                        <!-- ⚠ 走素材庫時封面**隨「儲存此題」寫入**，不像舊路徑是按了就存。
                             不講出來的話老師選完直接離開，改動就沒了。 -->
                        <div v-if="usesMediaLibrary" class="lbl mt-1">
                            {{ $t('unit.quest.cover_saves_with_quest') }}
                        </div>
                        <div v-else class="lbl mt-1">{{ $t('unit.quest.thumb_note') }}</div>

                        <!-- 移除素材後墊底的是舊 thumbnail_url，不是預設圖（規格 §4.3）。
                             轉換過的題目兩者共存，所以這個提示會真的出現。 -->
                        <div v-if="usesMediaLibrary && coverIsLegacyFallback" class="cover-legacy">
                            {{ $t('unit.quest.cover_legacy_fallback') }}
                        </div>

                        <input ref="thumbFile" class="d-none" type="file"
                            :accept="THUMB_ACCEPT" @change="onThumbPicked">

                        <!-- ⚠ `:ratio` 是**封面格子會被畫成的比例**，不是圖片本身的：
                             `MediaSlot` 的縮圖是 104×78 ＝ 4:3（見 VF9 的對照表）。 -->
                        <MediaPicker v-if="usesMediaLibrary" :show.sync="coverPickerOpen"
                            :ratio="4 / 3"
                            :value="coverMediaId" purpose="cover" @select="onCoverSelected" />
                        <!-- 改完走 onMediaLoaded 更新快照，預覽與檔名才會跟著變 -->
                        <MediaEditDialog v-if="usesMediaLibrary" :show.sync="coverEditOpen"
                            :media="coverSlotMedia" @saved="onMediaLoaded" />
                    </div>
                </div>
            </div>

            <!-- 模式專屬設定區（規格 §7.2）。位置在題目基本資料與畫格工作區之間：
                 老師的順序是「這一題要用什麼素材」→「學生要寫什麼」。 -->
            <QuestModeSettings v-if="showModeSettings" v-model="modeSettings"
                :mode="questMode" :media-map="mediaMap" @media-loaded="onMediaLoaded" />

            <!-- 互動影片問答。接在模式設定之後：那裡選的是主影片，
                 這裡是影片「裡面」的東西，順序跟老師的思路一致。
                 ⚠ 不是互動影片時元件自己回傳空，不必在這裡加條件。 -->
            <VideoQuestView :tree="videoQuest" :media-map="mediaMap"
                :video-media-id="modeSettings && modeSettings.video_media_id"
                :legacy-video-url="legacyVideoUrl"
                :locked-types="lockedQuestTypes"
                @media-loaded="onMediaLoaded"
                @audio-pending="audioPending = $event"
                @audio-retry="onAudioRetry" />

            <!-- 主工作區：左（畫格清單＋學生端示意）／右（編輯區）。
                 ⚠ 四種遊戲裡有三種沒有畫格，所以整塊要能隱藏（規格 §7.2）。 -->
            <div v-if="showFrameWork" class="work" data-rwd="work">
                <!-- 左上：畫格順序清單 -->
                <div class="card rail" data-rwd="rail">
                    <!-- ⚠ 找單字換一套文案。它的畫格不是老師編出來的分鏡，而是
                         **學生玩完之後自動存下來的那一篇紀錄**（建立題目時後端就補好了）。
                         沿用「學生會依序看到／第 N 格」會讓老師以為要自己排分鏡。 -->
                    <div class="rail-head">
                        <span class="rail-title">
                            {{ isWordsearch ? $t('unit.frame.wordsearch_order')
                                            : $t('unit.frame.student_order') }}
                        </span>
                    </div>
                    <div class="rail-hint">
                        {{ isWordsearch ? $t('unit.frame.wordsearch_order_hint')
                                        : $t('unit.frame.order_hint') }}
                    </div>

                    <draggable v-model="frameList" data-rwd="raillist" class="rail-list"
                        :delay="150" :delay-on-touch-only="true" handle=".pcard"
                        @end="onPanelDragEnd">
                        <div v-for="(frame, i) in frameList" :key="frame.key"
                            class="pcard" :class="{ 'pcard--on': i === selectedIndex }"
                            @click="selectFrame(i)">
                            <div class="pcard-top">
                                <span class="pcard-no">
                                    {{ frameNoLabel(frame, i) }}
                                </span>
                                <!-- 橘點只在有問題時顯示；完成的格子不顯示任何點 -->
                                <span v-if="panelHasIssue(frame)" class="dot-warn"></span>
                                <span class="qe-spacer"></span>
                                <!-- ⚠ 這兩顆一定要 stop，否則會連帶觸發卡片的 selectFrame -->
                                <button type="button" class="mv" :disabled="i === 0"
                                    @click.stop="movePanel(i, -1)">↑</button>
                                <button type="button" class="mv"
                                    :disabled="i === frameList.length - 1"
                                    @click.stop="movePanel(i, 1)">↓</button>
                            </div>
                            <div class="pcard-title" :class="{ 'is-empty': !frame.title }">
                                {{ frame.title || $t('unit.frame.unnamed') }}
                            </div>
                            <div class="pcard-type">{{ frameTypeLabel(frame, i) }}</div>
                            <div class="pcard-sum">{{ panelSummary(frame) }}</div>
                        </div>
                    </draggable>

                    <button v-if="frameList.length < MAX_FRAME" type="button" class="add-panel"
                        @click="typePickerOpen = true">
                        {{ $t('unit.frame.add_panel') }}
                    </button>
                    <!-- 滿 4 格時原地換成說明文字，按鈕不消失（設計稿明示） -->
                    <div v-else class="add-panel add-panel--full">
                        <div>{{ $t('unit.frame.panel_full') }}</div>
                        <div class="add-panel-hint">{{ $t('unit.frame.panel_full_hint') }}</div>
                    </div>
                </div>

                <!-- 左下：學生端示意 -->
                <StudentPreview class="preview" data-rwd="pv"
                    :frame="selectedFrame"
                    :index="selectedIndex"
                    :game-record="isGameRecord(selectedFrame, selectedIndex)"
                    :sample-list="visibleSamples"
                    :active-sample="activeSample"
                    :active-field-name="activeFieldName"
                    :behavior-type="selectedBehaviorType"
                    :behavior-count-map="behaviorCountMap" />

                <!-- 右：畫格編輯區 -->
                <div class="card editor">
                    <div v-if="!selectedFrame" class="ed-empty">
                        {{ frameList.length ? $t('unit.quest.select_frame')
                            : $t('unit.quest.frame_empty') }}
                    </div>
                    <template v-else>
                        <div class="ed-head">
                            <span class="ed-no">
                                {{ frameNoLabel(selectedFrame, selectedIndex) }}
                            </span>
                            <span class="ed-type">
                                {{ frameTypeLabel(selectedFrame, selectedIndex) }}
                            </span>
                            <v-menu :key="selectedFrame.key" offset-y max-width="420"
                                :close-on-content-click="false">
                                <template #activator="{ on, attrs }">
                                    <v-btn icon small class="ed-type-help" v-bind="attrs" v-on="on"
                                        :aria-label="$t('unit.frame.type_help')">
                                        <v-icon size="19">mdi-information-outline</v-icon>
                                    </v-btn>
                                </template>
                                <div class="ed-type-info">
                                    <div class="ed-type-info-title">{{ $t('unit.frame.type_help') }}</div>
                                    <div class="ed-type-info-hint">{{ $t('unit.frame.type_help_browse') }}</div>
                                    <div v-if="isGameRecord(selectedFrame, selectedIndex)"
                                        class="ed-type-info-current">
                                        <strong>{{ frameTypeLabel(selectedFrame, selectedIndex) }}</strong>
                                        <div>{{ $t('unit.frame.wordsearch_frame_desc') }}</div>
                                    </div>
                                    <details v-for="type in frameHelpTypes" :key="type"
                                        class="ed-type-detail"
                                        :open="type === selectedFrame.type
                                            && !isGameRecord(selectedFrame, selectedIndex)">
                                        <summary>{{ $t(`quest.main.${type}_frame`) }}</summary>
                                        <div>{{ $t(`quest.edit.${type}_frame_description`) }}</div>
                                    </details>
                                </div>
                            </v-menu>
                            <span class="qe-spacer"></span>
                            <!-- 型別建立後不可更改，所以沒有「更換型別」；一題至少留 1 格 -->
                            <button type="button" class="btn-danger"
                                :disabled="frameList.length <= 1"
                                @click="askDeleteFrame(selectedIndex)">
                                {{ $t('unit.frame.delete_panel') }}
                            </button>
                        </div>

                        <div class="two-col mt-4" data-rwd="two">
                            <div class="qe-col">
                                <div class="lbl">{{ frameTitleLabel }}</div>
                                <input class="in in--lg" type="text" v-model="selectedFrame.title"
                                    :maxlength="FRAME_TITLE_MAX">
                                <div class="cnt">
                                    {{ selectedFrame.title.length }} / {{ FRAME_TITLE_MAX }}
                                </div>
                            </div>
                            <div class="qe-col">
                                <div class="lbl">{{ frameDescLabel }}</div>
                                <textarea class="in ta" v-model="selectedFrame.description"
                                    :maxlength="DESC_MAX"></textarea>
                                <div class="cnt">
                                    {{ selectedFrame.description.length }} / {{ DESC_MAX }}
                                </div>
                            </div>
                        </div>

                        <!-- 型別專屬區塊 -->
                        <div v-if="hasTypeSection" class="ed-section">
                            <template v-if="selectedFrame.type === 'picture'">
                                <div class="sec-title">{{ $t('unit.frame.picture_note_title') }}</div>
                                <div class="sec-desc">{{ $t('unit.frame.picture_note') }}</div>
                            </template>

                            <template v-else-if="selectedFrame.type === 'feeling'">
                                <div class="sec-title">{{ $t('unit.frame.feeling_note_title') }}</div>
                                <div class="sec-desc">{{ $t('unit.frame.feeling_note') }}</div>
                            </template>

                            <template v-else>
                                <div class="sec-title">{{ $t('unit.frame.behavior_types') }}</div>
                                <div class="sec-desc">{{ $t('unit.frame.action_types_help') }}</div>
                                <div class="act-row">
                                    <button v-for="bt in behaviorTypes" :key="bt" type="button"
                                        class="act"
                                        :class="{
                                            'act--has': behaviorCount(bt) > 0,
                                            'act--on': bt === selectedBehaviorType,
                                        }"
                                        @click="selectBehaviorType(bt)">
                                        {{ behaviorLabel(bt) }} {{ behaviorCount(bt) }}
                                    </button>
                                </div>
                            </template>
                        </div>

                        <!-- 句型範例區。⚠ 六種型別裡只有 feeling（心情畫格）沒有範例，
                             picture 是有的——設計稿的表格在這裡寫錯了。 -->
                        <div v-if="selectedFrame.type !== 'feeling'" class="ed-section">
                            <div class="sec-head">
                                <span class="sec-title">{{ sampleSectionTitle }}</span>
                                <span class="qe-spacer"></span>
                                <button v-if="showSampleArea && !isSampleFull" type="button"
                                    class="btn-outline" @click="addSample">
                                    + {{ $t('unit.frame.add_sample') }}
                                </button>
                                <span v-else-if="showSampleArea" class="rail-count">
                                    {{ $t('unit.frame.add_sample_full') }}
                                </span>
                            </div>

                            <div v-if="!showSampleArea" class="sec-desc">
                                {{ $t('unit.frame.behavior_pick_type') }}
                            </div>
                            <template v-else>
                                <!--
                                    說明只在這裡講一次，不在每張範例卡重複。
                                    分兩行：第一行講「替換欄位是什麼」，第二行講「怎麼做」。
                                -->
                                <div class="sec-desc sec-desc--tight">
                                    {{ $t('unit.frame.sample_help') }}
                                </div>
                                <div class="sec-desc">{{ $t('unit.frame.sample_help_how') }}</div>
                                <ExampleCard v-for="(sample, si) in visibleSamples"
                                    :key="sample.key"
                                    :content="sample.content"
                                    :option="sample.option"
                                    :index="si"
                                    :active="activeSample === sample"
                                    @activate="activeSample = sample"
                                    @change="onSampleChange(sample, $event)"
                                    @delete="deleteSample(sample)" />
                            </template>
                        </div>
                    </template>
                </div>
            </div>

            <!-- 畫格由系統合成（例如 stage quest 2286）：學生看完影片後，系統把事件／情緒／行動
                 的答案合成進第一個畫格。老師不能新增或編輯畫格，所以整塊換成說明。 -->
            <v-alert v-else-if="isComposedFrame" text dense
                :type="frameList.length ? 'info' : 'warning'" class="mt-4">
                <div class="font-weight-medium">{{ $t('unit.quest.composed_title') }}</div>
                <div class="text-body-2">
                    {{ frameList.length ? $t('unit.quest.composed_body') : $t('unit.quest.composed_missing') }}
                </div>
            </v-alert>
        </template>

        <FrameTypePicker v-model="typePickerOpen" @pick="addFrame" />

        <v-dialog v-model="deleteFrameDialog" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">
                    {{ $t('unit.quest.delete_frame_title') }}
                </v-card-title>
                <v-card-text>
                    {{ $t('unit.quest.delete_frame_message', { n: deleteFrameSampleCount }) }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="deleteFrameDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" class="normal-case" @click="confirmDeleteFrame">
                        {{ $t('button.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="leaveDialog" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unit.quest.leave_title') }}</v-card-title>
                <v-card-text>{{ $t('unit.quest.leave_message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="resolveLeave(false)">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" text class="normal-case" @click="resolveLeave(true)">
                        {{ $t('unit.quest.leave_confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- 互動問答被刪光時存檔前的提醒：後端會把整棵樹拿掉，無法復原 -->
        <v-dialog v-model="clearVqDialog" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unit.quest.clear_vq_title') }}</v-card-title>
                <v-card-text>{{ $t('unit.quest.clear_vq_message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="clearVqDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" class="normal-case" @click="confirmClearVq">
                        {{ $t('unit.quest.clear_vq_confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="saveFailedDialog" width="480">
            <v-card>
                <v-card-title class="text-h6">{{ $t('unit.quest.save_failed') }}</v-card-title>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="saveFailedDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="primary" class="normal-case"
                        :loading="isSaving" @click="retrySave">
                        {{ $t('unit.list.reload') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SnackMsg ref="snack" />
    </div>
</template>

<script>
import { videoQuestForSave } from './videoQuestUtils';
import draggable from 'vuedraggable';
import ClickOutside from 'vuetify/lib/directives/click-outside';
import { diaryServices } from '@/services/diary.js';
import SnackMsg from '@/components/console/diary/component/SnackMsg';
import ExampleCard from '@/components/console/diary/unit/ExampleCard';
import {
    DEFAULT_QUEST_THUMBNAIL, THUMB_ACCEPT, THUMB_MAX_MB, THUMB_MAX_BYTES,
} from '@/components/console/diary/unit/unitConstants';
import { templateUnitServices } from '@/services/templateUnit.js';
import StudentPreview from '@/components/console/diary/unit/StudentPreview';
import FrameTypePicker from '@/components/console/diary/unit/FrameTypePicker';
import MediaPicker from '@/components/console/diary/media/MediaPicker';
import {
    showsFrameWork, minFrameCount, hasModeSettings, normalizeSettings, toPayload,
    validateSettings, MODE_VIDEO, composesFrameFromTypes, composeLockedTypes,
} from '@/components/console/diary/unit/questModeUtils';
import MediaSlot from '@/components/console/diary/unit/MediaSlot';
import MediaEditDialog from '@/components/console/diary/media/MediaEditDialog';
import QuestModeSettings from '@/components/console/diary/unit/QuestModeSettings';
import VideoQuestView from '@/components/console/diary/unit/VideoQuestView';
import { audioPollStep, FIRST_DELAY_MS as AUDIO_POLL_FIRST_DELAY_MS } from '@/components/console/diary/unit/audioPolling';

const NARROW_NOTICE_KEY = 'unit_narrow_notice_dismissed';
const MAX_FRAME = 4;
const MAX_SAMPLE = 3;
const QUEST_TITLE_MAX = 60;
const FRAME_TITLE_MAX = 80;
const DESC_MAX = 200;
const BEHAVIOR_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const BEHAVIOR_TYPE_GENERAL = 11;

let localKeySeed = 0;

/**
 * 題目層 —— v4 版面（support/關卡編輯/README.md）。
 *
 * 兩欄：左邊「畫格順序清單」＋「學生端示意」（sticky），右邊是選中畫格的編輯區。
 * 範例**不再用手風琴**——設計稿明示 v3 的手風琴要拿掉，改成範例卡直接並排展開。
 *
 * ⚠ 型別 key 一律用資料庫的：`story`＝情意、`behavior`＝行動、`story_2`＝心情敘寫、
 * `picture`＝圖片、`feeling`＝心情。設計稿用的是另一組 key，而且它的 `feeling`
 * 指的是我們的 `story`，意思剛好相反。
 * **六種型別裡只有 `feeling` 沒有句型範例**，`picture` 有（設計稿的表格寫錯）。
 */
export default {
    name: 'UnitQuestEdit',
    components: {
        draggable, SnackMsg, ExampleCard, StudentPreview, FrameTypePicker,
        MediaPicker, QuestModeSettings, MediaSlot, MediaEditDialog,
        VideoQuestView,
    },
    /// 這個專案沒有別處用到，不倚賴 vuetify-loader 的自動註冊，直接掛上
    directives: { ClickOutside },
    data() {
        return {
            homeworkId: '',
            questId: '',
            homeworkName: '',
            breadcrumbsItems: [],
            isLoading: true,
            loadFailed: false,
            isSaving: false,
            /**
             * VF8 規格 21：還有幾句語音不是「就緒」（待更新／未產生／失敗）。
             * 由 `VideoQuestView` 回報——它才知道每一句的狀態。
             */
            audioPending: 0,
            audioRetryRequested: false,
            frameHelpTypes: ['feeling', 'event', 'story', 'behavior', 'story_2', 'picture'],
            unit: null,
            unitName: '',
            thumbnailUrl: '',
            isUploadingThumb: false,
            /// 題目模式（`resources.mode`）。**B7 之前的舊題目是 null。**
            /// ⚠ 封面圖走哪條路完全由它決定，見 usesMediaLibrary。
            questMode: null,
            coverMediaId: null,
            coverUrl: '',
            coverPickerOpen: false,
            coverEditOpen: false,
            /// 模式專屬設定（規格 §4.2 的儲存形狀）。舊題目也會有一份正規化過的空值，
            /// 但 `usesMediaLibrary` 為 false 時不會被送出（見 buildPayload）。
            modeSettings: normalizeSettings(null, null),
            /// 這一題引用到的素材，key 是 media_id 的字串。後端一次帶出來的
            /// （沒有「讀單筆素材」的端點，逐筆查一題最多要 40 趟）。
            mediaMap: {},
            /// 舊的手工影片題目只有 `resources.video_link`、沒有 `video_media_id`，
            /// 影片問答區用它唯讀地播放（題目詳情的 `legacy_video_url`）。不進 payload。
            legacyVideoUrl: '',
            /// 題目詳情的 `is_video_only`。只給沒有 mode 的舊題目判斷畫格區用，不進 payload。
            isVideoOnly: false,
            /// 語音狀態的定時查詢（見 audioPolling.js）。`hidden` = 分頁不可見時跳過的那一輪，切回來補查
            audioPoll: { timer: null, startedAt: 0, attempt: 0, hidden: false },
            /// 互動影片問答的樹（題目詳情的 `video_quest`），不是互動影片就是 null。
            /// ⚠ 它**會**進 dirty 比對——`buildPayload` 把它序列化進 payload，
            /// 而 `isDirty` 比對的就是那個字串。
            videoQuest: null,
            /// 載入時後端有沒有這棵樹。刪光小題時要靠它分辨「清空」與「新題目還沒加題」。
            hadVideoQuest: false,
            clearVqDialog: false,
            DEFAULT_THUMB: DEFAULT_QUEST_THUMBNAIL,
            THUMB_ACCEPT: THUMB_ACCEPT,
            THUMB_MAX_MB: THUMB_MAX_MB,
            questIndex: 1,
            questTitle: '',
            questDescription: '',
            frameList: [],
            selectedIndex: -1,
            selectedBehaviorType: null,
            /// 哪一張範例卡是當前編輯對象（決定操作列出現在哪張卡上）
            activeSample: null,
            /// ExampleCard 目前選到的欄位名稱，只給學生端示意用來加深標記
            activeFieldName: null,
            baseline: '',
            typePickerOpen: false,
            deleteFrameDialog: false,
            deleteFrameIndex: -1,
            leaveDialog: false,
            leaveResolve: null,
            saveFailedDialog: false,
            checkOpen: false,
            narrowNoticeDismissed: false,
            behaviorTypes: BEHAVIOR_TYPES,
            MAX_FRAME,
            QUEST_TITLE_MAX,
            FRAME_TITLE_MAX,
            DESC_MAX,
        };
    },
    computed: {
        /**
         * 封面圖要走素材庫還是既有的直接上傳。
         *
         * ⚠ **判斷依據是「這一題有沒有 mode」，不是功能開關。**
         * `cover_media_id` 隨「儲存此題」寫入，而後端的 `saveUnitQuest` 對**沒有 mode
         * 的題目整段跳過模式邏輯**（規格 §9）——送了會被靜默丟掉，老師會以為存好了。
         * 所以舊題目必須繼續走 `uploadQuestThumbnail` 那支獨立端點。
         */
        usesMediaLibrary() {
            return !!this.questMode;
        },
        /**
         * 預覽要顯示哪一張。**順序與學生端的解析一致**（規格 §4.3）：
         * `cover_media_id` 優先，其次舊的 `thumbnail_url`，都沒有才用預設圖。
         */
        coverPreviewUrl() {
            return this.coverUrl || this.thumbnailUrl || this.DEFAULT_THUMB;
        },
        /**
         * 目前顯示的是「舊的 thumbnail_url 在墊底」。
         *
         * ⚠ 這個狀態會出現在**轉換過的題目**上：B13 會保留 `thumbnail_url` 並加上
         * `cover_media_id`（兩者共存，`thumbnail_url` 是 fallback）。所以老師把封面素材
         * 移除之後，學生端**不會**退回預設圖，而是看到那張舊圖——那是 §4.3 的既有行為，
         * 不是 bug。UI 要把這件事講出來，否則老師會以為移除沒生效。
         */
        coverIsLegacyFallback() {
            return !this.coverUrl && !!this.thumbnailUrl;
        },
        /**
         * 餵給 MediaSlot 的封面素材。
         *
         * ⚠ 從 `mediaMap` 查而不是自己組一個：`mediaMap` 是後端在題目詳情裡一次帶出來的
         * （含 `status` / `process_status`），MediaSlot 靠那兩欄標「已下架」與「處理中」。
         * 自己組會少掉那些資訊。剛挑或剛上傳的素材由 `onMediaLoaded` 補進去。
         *
         * ⚠ 查不到就回 `null`（顯示未選狀態）。這包含「舊的 `thumbnail_url` 墊底」的情況
         * ——那時 MediaSlot 顯示未選，而下面的 `cover_legacy_fallback` 提示會說明
         * 學生實際會看到舊縮圖。把舊縮圖塞進 MediaSlot 會讓「移除」變成一顆按了沒反應的鈕。
         */
        coverSlotMedia() {
            return this.coverMediaId
                ? (this.mediaMap[String(this.coverMediaId)] || null)
                : null;
        },
        /**
         * 要不要顯示畫格工作區（規格 §7.2 的表）。
         *
         * ⚠ **找單字一律顯示**（唯一的「遊戲 + 寫日記」組合），
         * **影片／PDF 看 `require_writing`**，其餘三種遊戲隱藏。
         * ⚠ 沒有 mode 的舊題目照舊顯示，**只有** `is_video_only` 的舊影片題隱藏（見 `requireWriting`）。
         */
        showFrameWork() {
            /// 畫格由系統合成的題目不給編（老師不能新增或修改那一格），改顯示說明
            if (this.isComposedFrame) {
                return false;
            }
            return showsFrameWork(this.questMode, this.requireWriting);
        },
        /// 日記畫格是不是學生看完影片後由系統合成的（見 questModeUtils.composesFrameFromTypes）
        isComposedFrame() {
            const quests = (this.videoQuest && this.videoQuest.quests) || [];
            return composesFrameFromTypes(quests.map((q) => q.type));
        },
        /// 合成日記要用的小題，編輯器不給刪
        lockedQuestTypes() {
            const quests = (this.videoQuest && this.videoQuest.quests) || [];
            return composeLockedTypes(quests.map((q) => q.type));
        },
        /**
         * 學生看完要不要寫日記。有 mode 的題目看模式設定；沒有 mode 的舊題目看
         * 題目詳情的 `is_video_only`（舊資料的設定，學生端讀的就是它），編輯器不會改它。
         */
        requireWriting() {
            return this.questMode ? this.modeSettings.require_writing : !this.isVideoOnly;
        },
        /** 這一題是不是找單字。決定畫格工作區要用哪一套文案。 */
        isWordsearch() {
            return this.questMode === 'game_wordsearch';
        },
        frameTitleLabel() {
            return this.isGameRecord(this.selectedFrame, this.selectedIndex)
                ? this.$t('unit.frame.wordsearch_title_label')
                : this.$t('unit.quest.frame_title_label');
        },
        frameDescLabel() {
            return this.isGameRecord(this.selectedFrame, this.selectedIndex)
                ? this.$t('unit.frame.wordsearch_desc_label')
                : this.$t('unit.quest.frame_description_label');
        },
        /** 畫格數的下限。⚠ 與「顯示」是兩件事：找單字顯示工作區但下限是 0。 */
        minFrames() {
            /// 合成畫格的題目缺畫格只警告（checkRows），不擋存檔——老師也沒辦法自己補
            if (this.isComposedFrame) {
                return 0;
            }
            return minFrameCount(this.questMode, this.requireWriting);
        },
        /** 這個模式有專屬設定區嗎（規格 §7.2）。`writing` 與舊題目沒有。 */
        showModeSettings() {
            return hasModeSettings(this.questMode);
        },
        showNarrowNotice() {
            return this.$vuetify.breakpoint.xs && !this.narrowNoticeDismissed;
        },
        selectedFrame() {
            return this.frameList[this.selectedIndex] || null;
        },
        hasTypeSection() {
            const f = this.selectedFrame;
            return !!f && ['picture', 'feeling', 'behavior'].includes(f.type);
        },
        /// behavior 要先選行動類型才看得到範例區
        showSampleArea() {
            const f = this.selectedFrame;
            if (!f || f.type === 'feeling') {
                return false;
            }
            return f.type !== 'behavior' || this.selectedBehaviorType !== null;
        },
        visibleSamples() {
            const f = this.selectedFrame;
            if (!f) {
                return [];
            }
            if (f.type !== 'behavior') {
                return f.sample || [];
            }
            return (f.sample || []).filter((s) => s.behaviorType === this.selectedBehaviorType);
        },
        /// behavior 是每個行動類型各 3 個；其他型別是整個畫格 3 個
        isSampleFull() {
            return this.visibleSamples.length >= MAX_SAMPLE;
        },
        sampleSectionTitle() {
            const f = this.selectedFrame;
            if (f && f.type === 'behavior' && this.selectedBehaviorType !== null) {
                return this.$t('unit.frame.samples_of',
                    { type: this.behaviorLabel(this.selectedBehaviorType) });
            }
            return this.$t('unit.frame.samples');
        },
        /** 原本有互動問答、現在被刪光——存檔會把整棵樹拿掉 */
        clearsVideoQuest() {
            return this.hadVideoQuest && !!this.videoQuest
                && (this.videoQuest.quests || []).length === 0;
        },
        isDirty() {
            return JSON.stringify(this.buildPayload()) !== this.baseline;
        },
        canSave() {
            return !this.isSaving && !this.isLoading && !this.loadFailed
                && !this.blockRows.length && (this.isDirty || this.audioRetryRequested);
        },
        saveLabel() {
            if (this.isSaving) return this.$t('unit.quest.saving');
            if (this.isDirty || this.audioRetryRequested) {
                return this.audioPending
                    ? this.$t('unit.quest.save_with_audio') : this.$t('unit.quest.save');
            }
            return this.baseline
                ? this.$t('unit.quest.already_saved') : this.$t('unit.quest.save');
        },
        /* ---------- 儲存前的檢查 ---------- */

        /**
         * 兩類問題（steps 決策表）：
         * - `block`：**會讓資料壞掉、或後端會直接拒絕**，擋儲存。
         *   ⚠ 「後端會拒絕」這一半原本漏了，症狀是徽章顯示綠色、儲存鈕可按，
         *   按下去卻拿到 400（2026-08-12 修）。後端的硬性條件見
         *   `TemplateUnitService.validateUnitQuestInput`：畫格數必須 1–4、
         *   心情畫格不得有範例。
         * - `warn`：只是還沒填完，不擋。標題空、範例空白、欄位沒出現在句子裡。
         *
         * ⚠ 一律從 `{content, option}` 現算，不吃 ExampleCard 送上來的 `blocking`——
         * 那個只有被編輯過的卡才有值，剛載入的卡會漏檢。
         */
        checkRows() {
            const rows = [];
            if (!this.questTitle.trim()) {
                /// ⚠ **有 mode 的題目是 `block`，舊題目是 `warn`**（2026-09-04 修）。
                /// 這一條要**逐字對齊後端**（`TemplateUnitService.saveUnitQuest`：
                /// `if (storedMode) { if (input.questTitle.trim() === '') throw }`）。
                ///
                /// 在這之前一律是 `warn`，於是儲存按鈕沒有 disable、老師按下去才拿到
                /// 400 與一個「儲存失敗」對話框——**檢查清單明明是黃燈卻存不了**。
                ///
                /// ⚠ **不能無條件改成 `block`**：舊題目（B7 之前建的，沒有 mode）
                /// 允許空標題，既有資料就有。對它們擋下來會讓老師**完全存不了**那些題目，
                /// 而且他無法理解為什麼——後端的註解已經記了同一件事。
                rows.push({
                    level: this.questMode ? 'block' : 'warn',
                    label: this.$t('unit.validation.at_quest'),
                    reason: this.$t('unit.validation.quest_title_required'),
                    frame: -1,
                });
            }
            /// ⚠ **下限依模式**（規格 §7.2、§8）。原本寫死「0 格就擋」，那對四種遊戲
            /// 模式不成立——照舊會讓那些題目**永遠存不了**，而錯誤訊息會說
            /// 「至少要一個畫格」，完全看不出真正的原因。
            /// ⚠ **找單字的下限是 1 而不是 0**（2026-09-01 改回來）。這一段原本寫著
            /// 「學生端會自己補虛擬畫格」——那句話只對了一半：不會報錯，但虛擬畫格沒有
            /// `id`，日記就對應不到任何 `diary_content_sample`。建立題目時後端會自動補
            /// 一格，這個下限是防止老師把它刪掉。
            if (this.frameList.length < this.minFrames) {
                rows.push({
                    level: 'block',
                    label: this.$t('unit.validation.at_quest'),
                    reason: this.$t('unit.validation.frame_required'),
                    frame: -1,
                });
            }
            /// 模式專屬設定的驗證（規格 §8）。⚠ 規則放在 questModeUtils，與後端對齊；
            /// 這裡只負責翻譯與貼上 label。
            if (this.questMode) {
                validateSettings(this.questMode, this.modeSettings, this.mediaMap)
                    .forEach((row) => {
                        rows.push({
                            level: row.level,
                            label: this.$t('unit.validation.at_settings'),
                            reason: this.$t(`unit.settings.${row.key}`, row.params || {}),
                            frame: -1,
                        });
                    });
            }
            if (this.isComposedFrame) {
                /// 畫格不給編，逐格的檢查（標題、範例）對老師沒有意義；只講「缺了那一格」
                if (!this.frameList.length) {
                    rows.push({
                        level: 'warn',
                        label: this.$t('unit.validation.at_quest'),
                        reason: this.$t('unit.quest.composed_missing'),
                        frame: -1,
                    });
                }
                return rows;
            }
            this.frameList.forEach((frame, i) => {
                const at = this.$t('unit.validation.at_frame',
                    { n: i + 1, title: frame.title || this.$t('unit.frame.unnamed') });
                if (!frame.title.trim()) {
                    rows.push({
                        level: 'warn',
                        label: at,
                        reason: this.$t('unit.validation.frame_title_required'),
                        frame: i,
                    });
                }
                /// 既有資料有 21 個心情畫格帶著範例，而後端明文拒絕這種組合。
                /// 編輯器沒有地方編這些範例，所以存檔時會丟掉——先講，不要靜默刪。
                if (frame.type === 'feeling' && (frame.sample || []).length > 0) {
                    rows.push({
                        level: 'warn',
                        label: at,
                        reason: this.$t('unit.check.feeling_sample_dropped',
                            { n: frame.sample.length }),
                        frame: i,
                    });
                }
                let blank = 0;
                (frame.sample || []).forEach((sp) => {
                    if (!(sp.content || '').trim()) {
                        blank += 1;
                    }
                    this.sampleIssues(sp).forEach((issue) => {
                        rows.push(Object.assign({ label: at, frame: i, sample: sp }, issue));
                    });
                });
                if (blank) {
                    rows.push({
                        level: 'warn',
                        label: at,
                        reason: this.$t('unit.check.sample_blank', { n: blank }),
                        frame: i,
                    });
                }
            });
            return rows;
        },
        blockRows() {
            return this.checkRows.filter((r) => r.level === 'block');
        },
        warnRows() {
            return this.checkRows.filter((r) => r.level === 'warn');
        },
        badgeText() {
            if (this.blockRows.length) {
                return this.$t('unit.check.blocked', { n: this.blockRows.length });
            }
            if (this.warnRows.length) {
                return this.$t('unit.check.pending', { n: this.warnRows.length });
            }
            return `✓ ${this.$t('unit.check.ok')}`;
        },
        /// 學生端示意要知道每個行動類型有沒有範例（實線／虛線）
        behaviorCountMap() {
            const map = {};
            const f = this.selectedFrame;
            if (f && f.type === 'behavior') {
                (f.sample || []).forEach((sp) => {
                    map[sp.behaviorType] = (map[sp.behaviorType] || 0) + 1;
                });
            }
            return map;
        },
        deleteFrameSampleCount() {
            const f = this.frameList[this.deleteFrameIndex];
            return f ? (f.sample || []).length : 0;
        },
    },
    async created() {
        this.homeworkId = this.$route.params.id;
        this.questId = this.$route.params.questId;
        this.narrowNoticeDismissed = sessionStorage.getItem(NARROW_NOTICE_KEY) === '1';
        await this.load();
        this.startAudioPolling();
    },
    mounted() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopAudioPolling();
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isDirty) {
            next();
            return;
        }
        this.leaveResolve = next;
        this.leaveDialog = true;
    },
    methods: {
        async load() {
            this.isLoading = true;
            this.loadFailed = false;
            try {
                const [detailRes, questRes] = await Promise.all([
                    diaryServices.getHomeworkDetail(this.homeworkId),
                    diaryServices.getSampleQuestDetail(this.homeworkId, this.questId),
                ]);
                this.homeworkName = detailRes?.data?.name || '';
                const quest = questRes?.data || {};
                this.unit = quest.unit;
                this.unitName = quest.unit_name || '';
                this.questIndex = quest.quest_index || 1;
                this.thumbnailUrl = quest.thumbnail_url || '';
                this.questMode = quest.mode || null;
                /// ⚠ 兩者都要收（決策 20）：`cover_media_id` 是新路徑、
                /// `thumbnail_url` 是舊資料的 fallback，**舊值不刪**。
                this.coverMediaId = quest.cover_media_id || null;
                this.coverUrl = quest.cover_url || '';
                this.modeSettings = normalizeSettings(this.questMode, quest.mode_settings);
                this.mediaMap = quest.media || {};
                this.legacyVideoUrl = quest.legacy_video_url || '';
                this.isVideoOnly = quest.is_video_only === true;
                /// ⚠ 新建的 video 模式題目後端回 null（辨識靠 `video_quest` 這個 key 在不在），
                /// 給一棵空樹，否則 `VideoQuestView` 的 `v-if="tree"` 讓老師無從加第一題。
                /// 空樹不會送出（見 buildPayload），所以不影響 dirty 比對。
                this.hadVideoQuest = !!quest.video_quest;
                this.videoQuest = quest.video_quest
                    || (this.questMode === MODE_VIDEO ? { quests: [] } : null);
                this.questTitle = quest.title || '';
                this.questDescription = quest.description || '';
                this.frameList = (quest.sample_list || []).map((item) => ({
                    key: `k${localKeySeed += 1}`,
                    contentId: item.id || null,
                    type: item.type,
                    title: item.title || '',
                    description: item.description || '',
                    sample: (item.sample || []).map((sp) => ({
                        key: `s${localKeySeed += 1}`,
                        id: sp.id || null,
                        content: sp.content || '',
                        /// ⚠ option 原樣帶進來——送空陣列會把老師既有的替換欄位清掉
                        option: sp.option || [],
                        behaviorType: sp.behavior_type != null ? sp.behavior_type : null,
                    })),
                }));
                this.selectFrame(this.frameList.length ? 0 : -1);
                this.baseline = JSON.stringify(this.buildPayload());
                this.buildBreadcrumbs();
            } catch (err) {
                this.loadFailed = true;
            } finally {
                this.isLoading = false;
            }
        },

        buildBreadcrumbs() {
            this.breadcrumbsItems = [
                { text: this.$t('main_menu.quest'), disabled: false, href: '/diarySample' },
                {
                    text: this.homeworkName,
                    disabled: false,
                    /// 範本內容主頁（舊版與關卡式共用），不是 /diarySample/:id
                    href: `/diary/homework/${this.homeworkId}/quest`,
                },
                {
                    text: this.$t('unit.list.page_title'),
                    disabled: false,
                    href: `/diarySample/${this.homeworkId}/units`,
                },
                {
                    text: this.$t('unit.quest.breadcrumb_quest',
                        { n: this.unit, m: this.questIndex }),
                    disabled: true,
                },
            ];
        },

        buildPayload(dropBlank = false) {
            const payload = {
                quest_id: Number(this.questId),
                quest_title: this.questTitle,
                quest_description: this.questDescription,
                lang: localStorage.getItem('locale') || 'zh',
                sample_list: this.frameList.map((frame) => ({
                    content_id: frame.contentId,
                    type: frame.type,
                    title: frame.title,
                    description: frame.description,
                    /// ⚠ 心情畫格一律送空陣列：後端會拒絕「feeling 畫格有範例」，
                    /// 原樣送回既有的 21 筆會讓那些題目永遠存不起來
                    sample: (frame.type === 'feeling' ? [] : (frame.sample || []))
                        .filter((sp) => !dropBlank || sp.content)
                        .map((sp) => {
                            const out = { id: sp.id, content: sp.content, option: sp.option };
                            if (sp.behaviorType != null) {
                                out.behavior_type = sp.behaviorType;
                            }
                            return out;
                        }),
                })),
            };
            /// ⚠ **只有有 mode 的題目才送 `cover_media_id`。** 沒有 mode 的題目後端會
            /// 整段跳過模式邏輯，送了不會有作用；而它進了 payload 就會進 dirty 比對的
            /// baseline，於是舊題目一開啟就被判定「有未儲存的變更」。
            ///
            /// ⚠ 也**只有這一個 key**：`mode` 刻意不送。後端收到與現況不同的 mode 會回 400
            /// （模式不可改，ADR 0002），而送一個一樣的值只是給「哪裡能改 mode」多開一個入口。
            /**
             * ⚠⚠ **傳輸格式與儲存格式不同**：儲存的 `answers` 是 map、key 是
             * `option.id`，但送出去時**答案掛在選項底下**（`option.answer`）。
             *
             * 理由是新增選項時前端猜不到 id——id 由後端配（決策 D4）。任一邊漏同步
             * 就是學生點下去 `undefined.isCorrect`（規格 §7 的既有災情）。
             * 掛在選項底下之後，那個不同步**在結構上不可能發生**。
             *
             * ⚠ `undefined` = 這次不動這棵樹。不是互動影片的題目不能送空物件——
             * 後端對「沒送」與「送了」的語意不同。
             *
             * ⚠ 空樹分兩種：新題目還沒加題 → 不送（不動）；原本有樹、被刪光 → 送
             * `quests: []`，後端會把整個 `video_quest` key 拿掉。
             */
            if (this.videoQuest
                && ((this.videoQuest.quests || []).length > 0 || this.hadVideoQuest)) {
                payload.video_quest = videoQuestForSave(this.videoQuest);
            }
            if (this.usesMediaLibrary) {
                payload.cover_media_id = this.coverMediaId;
                /// ⚠ **只送這個模式擁有的 key**（規格 §4.2）。後端會忽略不屬於該模式的
                /// key，所以送了不會出錯——但它們會進 dirty 比對的 baseline。
                Object.assign(payload, toPayload(this.questMode, this.modeSettings));
            }
            return payload;
        },

        /* ---------- 畫格清單 ---------- */

        /** 回傳這個範例的所有問題（可能不只一個） */
        sampleIssues(sample) {
            const out = [];
            const content = sample.content || '';
            const seen = [];
            (sample.option || []).forEach((op) => {
                const name = op.field || '';
                if (!name.trim()) {
                    out.push({
                        level: 'block',
                        reason: this.$t('unit.validation.field_name_required'),
                    });
                    return;
                }
                if (seen.includes(name)) {
                    out.push({
                        level: 'block',
                        reason: this.$t('unit.validation.field_name_duplicate', { name }),
                    });
                    return;
                }
                seen.push(name);
                const hits = content.split(`(${name})`).length - 1;
                if (hits > 1) {
                    /// 存了再載入會解析到第一個位置，欄位靜默前移
                    out.push({
                        level: 'block',
                        reason: this.$t('unit.validation.field_name_literal_conflict', { name }),
                    });
                } else if (hits === 0) {
                    out.push({
                        level: 'warn',
                        reason: this.$t('unit.check.field_orphan', { name }),
                    });
                }
            });
            return out;
        },

        /**
         * 這一格是不是「找單字玩完自動存下來的那一篇紀錄」。
         *
         * ⚠ **不是「找單字題的所有畫格」。** 自動補的只有**第一格**
         * （`createWordsearchFrameIfNeeded` 建的 `story` 格，`display_order = 1`）；
         * 老師之後還是可以自己加第 2–4 格，那些是一般畫格，要用一般文案。
         * ⚠ 同時檢查 `type === 'story'`：萬一老師把第一格刪了、剩下的變成別的型別，
         * 就退回一般文案而不是掛一個講錯的標籤。
         */
        isGameRecord(frame, index) {
            return this.isWordsearch && index === 0 && !!frame && frame.type === 'story';
        },

        /** 左側卡片與編輯區的編號。遊戲紀錄那一格不編號——它只有一格，「第 1 格」沒有意義。 */
        frameNoLabel(frame, index) {
            return this.isGameRecord(frame, index)
                ? this.$t('unit.frame.wordsearch_no')
                : this.$t('unit.frame.panel_n', { n: index + 1 });
        },

        /** 型別標籤。⚠ 遊戲紀錄實際上是 `story`，但對老師講「情意畫格」沒有意義。 */
        frameTypeLabel(frame, index) {
            return this.isGameRecord(frame, index)
                ? this.$t('unit.frame.wordsearch_type')
                : this.$t(`quest.main.${frame.type}_frame`);
        },

        panelHasIssue(frame) {
            if (!frame.title) {
                return true;
            }
            return (frame.sample || []).some(
                (sp) => !(sp.content || '').trim() || this.sampleIssues(sp).length);
        },

        /** 浮層每一列都可以跳到出問題的地方 */
        jumpTo(row) {
            this.checkOpen = false;
            if (row.frame < 0) {
                return;
            }
            this.selectFrame(row.frame);
            if (row.sample) {
                this.$nextTick(() => {
                    if (row.sample.behaviorType != null) {
                        this.selectedBehaviorType = row.sample.behaviorType;
                    }
                    this.activeSample = row.sample;
                });
            }
        },

        panelSummary(frame) {
            if (frame.type === 'feeling') {
                return this.$t('unit.frame.summary_mood');
            }
            const samples = frame.sample || [];
            const fields = samples.reduce((n, sp) => n + (sp.option || []).length, 0);
            return this.$t('unit.frame.summary_write', { n: samples.length, m: fields });
        },

        selectFrame(index) {
            this.selectedIndex = index;
            this.activeSample = null;
            this.activeFieldName = null;
            this.selectedBehaviorType = null;
            const frame = this.frameList[index];
            if (frame && frame.type === 'behavior') {
                /// 帶入第一個已有範例的行動類型，老師一進來就看得到東西
                const first = (frame.sample || [])[0];
                this.selectedBehaviorType = first ? first.behaviorType : null;
            }
            this.$nextTick(() => {
                this.activeSample = this.visibleSamples[0] || null;
            });
        },

        /** ↑↓ 移動畫格；選取狀態跟著被移動的那一格走 */
        movePanel(index, delta) {
            const target = index + delta;
            if (target < 0 || target >= this.frameList.length) {
                return;
            }
            const list = this.frameList.slice();
            const [moved] = list.splice(index, 1);
            list.splice(target, 0, moved);
            this.frameList = list;
            this.selectedIndex = target;
        },

        onPanelDragEnd(event) {
            if (event.oldIndex === event.newIndex) {
                return;
            }
            this.selectedIndex = event.newIndex;
        },

        addFrame(type) {
            this.typePickerOpen = false;
            if (this.frameList.length >= MAX_FRAME) {
                return;
            }
            this.frameList.push({
                key: `k${localKeySeed += 1}`,
                contentId: null,
                type,
                title: '',
                description: '',
                sample: [],
            });
            this.selectFrame(this.frameList.length - 1);
        },

        askDeleteFrame(index) {
            const frame = this.frameList[index];
            /// 空白格直接刪，不用問
            if (!frame.title && !frame.description && (frame.sample || []).length === 0) {
                this.removeFrame(index);
                return;
            }
            this.deleteFrameIndex = index;
            this.deleteFrameDialog = true;
        },

        confirmDeleteFrame() {
            this.removeFrame(this.deleteFrameIndex);
            this.deleteFrameDialog = false;
            this.deleteFrameIndex = -1;
        },

        removeFrame(index) {
            this.frameList.splice(index, 1);
            this.selectFrame(this.frameList.length
                ? Math.min(index, this.frameList.length - 1) : -1);
        },

        /* ---------- 行動類型 ---------- */

        behaviorLabel(type) {
            const name = this.$t(`behavior.type.${type}`);
            return type === BEHAVIOR_TYPE_GENERAL
                ? `${name}（${this.$t('unit.frame.behavior_type_general')}）`
                : name;
        },

        behaviorCount(type) {
            const f = this.selectedFrame;
            return f ? (f.sample || []).filter((sp) => sp.behaviorType === type).length : 0;
        },

        /** 點虛線（0 個範例）→ 選取並直接開一張空白卡；點實線 → 只切換 */
        selectBehaviorType(type) {
            this.selectedBehaviorType = type;
            this.activeSample = null;
            this.activeFieldName = null;
            if (this.behaviorCount(type) === 0) {
                this.addSample();
                return;
            }
            this.$nextTick(() => {
                this.activeSample = this.visibleSamples[0] || null;
            });
        },

        /* ---------- 範例 ---------- */

        addSample() {
            const frame = this.selectedFrame;
            if (!frame || this.isSampleFull) {
                return;
            }
            const sample = {
                key: `s${localKeySeed += 1}`,
                id: null,
                content: '',
                option: [],
                behaviorType: frame.type === 'behavior' ? this.selectedBehaviorType : null,
            };
            frame.sample.push(sample);
            this.activeSample = sample;
        },

        onSampleChange(sample, payload) {
            sample.content = payload.content;
            sample.option = payload.option;
        },

        deleteSample(sample) {
            const frame = this.selectedFrame;
            if (!frame) {
                return;
            }
            /// 用物件比對而不是索引——behavior 顯示的是過濾後的子集，索引對不上
            const index = frame.sample.indexOf(sample);
            if (index === -1) {
                return;
            }
            const removed = frame.sample.splice(index, 1)[0];
            if (this.activeSample === removed) {
                this.activeSample = this.visibleSamples[0] || null;
            }
            this.$refs.snack?.showActionMsg(
                'info',
                this.$t('unit.frame.sample_deleted'),
                this.$t('unit.undo.action'),
                () => {
                    frame.sample.splice(index, 0, removed);
                    this.activeSample = removed;
                });
        },

        /* ---------- 儲存 ---------- */

        save() {
            if (!this.canSave) return;
            if (this.clearsVideoQuest) {
                this.clearVqDialog = true;
                return;
            }
            this.doSave();
        },

        confirmClearVq() {
            this.clearVqDialog = false;
            this.doSave();
        },

        /**
         * VF8 規格 10：老師點了某一句的「↻ 重試」。
         *
         * ⚠ **沒有單句重試的端點，也不需要。** 後端 `QuestAudioService` 的 `usable`
         * 把 `failed` 排除在外，所以**再存一次檔那一句就會重排**。
         * 這裡只提示老師去按儲存——做一顆假的「重試中」轉圈反而會騙人。
         */
        onAudioRetry() {
            this.audioRetryRequested = true;
            this.$toastr.Add({
                msg: this.$t('unit.quest.audio_retry_hint'),
                type: 'info',
                timeout: 4000,
                clickClose: true,
                position: 'toast-top-right',
                classNames: ['animated', 'bounceInRight'],
            });
        },

        async doSave() {
            if (!this.canSave) return;
            this.isSaving = true;
            try {
                await diaryServices.addSampleQuest(this.homeworkId, this.buildPayload(true));
                await this.reloadAfterSave();
                this.audioRetryRequested = false;
                /// ⚠ 存檔成功可能讓這一題**從被停用恢復**：後端的 `syncBlockedMediaIds`
                /// 在換掉下架素材後會清掉 `blocked_media_ids` 並把 status 復原成 `1`。
                /// 全站橫幅的數字得跟著掉，否則老師修好了還一直看到那個警告。
                /// ⚠ 無條件發：這裡判斷不出「剛剛是不是被卡住的那一題」，
                /// 而摘要只是兩個 COUNT，多打一次很便宜。
                this.$root.$emit('blocked-quest-refresh');
                /// ⚠⚠ 有語音要產生時要換一句話。存檔**不會立刻產生語音**——
                /// worker 是 cron 觸發的（需求方 2026-09-21：不需要即時）。
                /// 不講的話老師會以為壞了，一直重按儲存。
                this.$toastr.Add({
                    msg: this.audioPending
                        ? this.$t('unit.quest.save_success_audio')
                        : this.$t('unit.quest.save_success'),
                    type: 'success',
                    timeout: 3000,
                    clickClose: true,
                    position: 'toast-top-right',
                    classNames: ['animated', 'bounceInRight'],
                });
            } catch (err) {
                this.saveFailedDialog = true;
            } finally {
                this.isSaving = false;
            }
        },

        async retrySave() {
            this.saveFailedDialog = false;
            await this.doSave();
        },

        /** 存完重讀，讓後端配的 id 回填；停在原本那一格 */
        async reloadAfterSave() {
            const keep = this.selectedIndex;
            await this.load();
            if (keep >= 0 && keep < this.frameList.length) {
                this.selectFrame(keep);
            }
            /// 存檔可能排了新的語音：重新開始一輪（間隔與 10 分鐘上限都從頭算）
            this.stopAudioPolling();
            this.startAudioPolling();
        },

        /* ---------- 語音狀態的定時查詢（audioPolling.js） ---------- */

        /// `mediaMap` 裡還有產生中的語音才開始查。已經在查就不重複開。
        startAudioPolling() {
            if (this.audioPoll.timer) {
                return;
            }
            const busy = Object.values(this.mediaMap).some((m) => m && m.kind === 'audio'
                && (m.process_status === 'pending' || m.process_status === 'processing'));
            if (!busy) {
                return;
            }
            this.audioPoll.startedAt = Date.now();
            this.audioPoll.attempt = 0;
            this.audioPoll.hidden = false;
            this.audioPoll.timer = setTimeout(this.pollAudio, AUDIO_POLL_FIRST_DELAY_MS);
        },

        stopAudioPolling() {
            clearTimeout(this.audioPoll.timer);
            this.audioPoll.timer = null;
            this.audioPoll.hidden = false;
        },

        async pollAudio() {
            /// ⚠ 不可見時不查（老師切去別的分頁），切回來由 onVisibilityChange 補查
            if (document.hidden) {
                this.audioPoll.timer = null;
                this.audioPoll.hidden = true;
                return;
            }
            const statuses = {};
            try {
                const res = await templateUnitServices.getQuestAudioStatus(this.homeworkId, this.questId);
                const media = (res && res.data && res.data.media) || {};
                for (const [id, fresh] of Object.entries(media)) {
                    /// ⚠ 只更新這幾筆的狀態欄位，不碰老師正在編輯的樹與文字（未存檔的修改不能被蓋掉）
                    this.$set(this.mediaMap, id, Object.assign({}, this.mediaMap[id], fresh));
                    statuses[id] = fresh.process_status;
                }
            } catch (err) {
                /// 這一輪查不到就當作還在產生中，下一輪再試；10 分鐘上限照樣會停
                statuses.unknown = 'pending';
            }
            const step = audioPollStep(statuses, Date.now() - this.audioPoll.startedAt, this.audioPoll.attempt);
            this.audioPoll.attempt += 1;
            if (!step.done) {
                this.audioPoll.timer = setTimeout(this.pollAudio, step.delayMs);
                return;
            }
            this.audioPoll.timer = null;
            if (step.notice) {
                this.$toastr.Add({
                    msg: step.notice.type === 'failed'
                        ? this.$t('unit.quest.audio_failed', { n: step.notice.count })
                        : this.$t('unit.quest.audio_ready'),
                    type: step.notice.type === 'failed' ? 'warning' : 'success',
                    timeout: 5000,
                    clickClose: true,
                    position: 'toast-top-right',
                    classNames: ['animated', 'bounceInRight'],
                });
            }
        },

        onVisibilityChange() {
            if (!document.hidden && this.audioPoll.hidden) {
                this.audioPoll.hidden = false;
                this.pollAudio();
            }
        },

        resolveLeave(shouldLeave) {
            this.leaveDialog = false;
            const next = this.leaveResolve;
            this.leaveResolve = null;
            if (next) {
                next(shouldLeave ? undefined : false);
            }
        },

        onBeforeUnload(event) {
            if (!this.isDirty) {
                return;
            }
            event.preventDefault();
            event.returnValue = '';
        },

        /// 題目清單改成獨立頁之後，返回就是回那一頁（原本的 sessionStorage 重開機制不需要了）
        backToList() {
            this.$router.push(this.unit
                ? `/diarySample/${this.homeworkId}/units/${this.unit}`
                : `/diarySample/${this.homeworkId}/units`);
        },

        /* ---------- 封面圖：素材庫路徑（隨「儲存此題」送出） ---------- */

        /**
         * 從素材庫選了一份。
         *
         * ⚠ 用 `@select`（帶整筆）而不是 `@input`（只帶 id）：預覽需要 `url`，
         * 只拿 id 就得再打一次 API——而目前**沒有「讀單筆素材」的端點**。
         */
        /**
         * 把一筆素材登記進 `mediaMap`。
         *
         * ⚠ **每一條「拿到新素材」的路徑都要經過這裡**：模式設定區的 `media-loaded`、
         * 封面的挑選與上傳。`mediaMap` 是載入時的快照，是所有預覽的唯一來源
         * ——漏掉任何一條，那條路徑選完的素材就會顯示成「還沒選」，
         * 而且要存檔重讀才會出現（2026-08-31 封面欄就漏過一次）。
         */
        onMediaLoaded(media) {
            if (!media || !media.media_id) {
                return;
            }
            /// Vue 2 要用 $set 才會觸發更新（新增 key 不是 reactive 的）
            this.$set(this.mediaMap, String(media.media_id), media);
        },

        onCoverSelected(media) {
            /// ⚠ **一定要先進 mediaMap。** 預覽（`coverSlotMedia`）是從那裡查的，
            /// 而 mediaMap 只是載入時的快照——不補進去的話剛選的素材查不到，
            /// MediaSlot 會顯示成「還沒選」，要存檔重讀才出現。
            this.onMediaLoaded(media);
            this.coverMediaId = media.media_id;
            this.coverUrl = media.url || '';
        },


        /**
         * 移除封面素材。
         *
         * ⚠ **只清 `cover_media_id`，不動 `thumbnail_url`。** 舊值是 fallback（規格 §4.3），
         * 而清掉它要打另一支端點、語意也不同（那是「刪掉上傳的檔案」）。
         * 清完之後若還有舊縮圖，預覽與學生端都會顯示它——UI 用
         * `cover_legacy_fallback` 把這件事講出來。
         */
        removeCover() {
            this.coverMediaId = null;
            this.coverUrl = '';
        },

        /* ---------- 封面圖：舊路徑（獨立 API，按了就存） ---------- */

        pickThumb() {
            this.$refs.thumbFile.value = '';
            this.$refs.thumbFile.click();
        },

        async onThumbPicked(event) {
            const file = (event.target.files || [])[0];
            if (!file) {
                return;
            }
            /// 前端先擋一次，省掉一趟上傳；後端的 bodyparser 也會擋
            if (file.size > THUMB_MAX_BYTES) {
                this.toastError(this.$t('unit.quest.thumb_too_big', { n: THUMB_MAX_MB }));
                return;
            }
            /// HEIC 在某些瀏覽器的 file.type 是空字串，所以副檔名也算數
            const okType = /^image\//.test(file.type)
                || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
            if (!okType) {
                this.toastError(this.$t('unit.quest.thumb_bad_type'));
                return;
            }

            this.isUploadingThumb = true;
            try {
                const res = await templateUnitServices.uploadQuestThumbnail(
                    this.homeworkId, this.questId, file);
                this.thumbnailUrl = res?.data?.thumbnail_url || '';
                this.$refs.snack?.showAlertMsg('success', this.$t('unit.quest.thumb_uploaded'));
            } catch (err) {
                this.toastError(this.$t('unit.quest.thumb_upload_failed'));
            } finally {
                this.isUploadingThumb = false;
            }
        },

        async removeThumb() {
            this.isUploadingThumb = true;
            try {
                await templateUnitServices.deleteQuestThumbnail(this.homeworkId, this.questId);
                this.thumbnailUrl = '';
                this.$refs.snack?.showAlertMsg('success', this.$t('unit.quest.thumb_removed'));
            } catch (err) {
                this.toastError(this.$t('unit.quest.thumb_remove_failed'));
            } finally {
                this.isUploadingThumb = false;
            }
        },

        closeCheck() {
            this.checkOpen = false;
        },

        dismissNarrowNotice() {
            this.narrowNoticeDismissed = true;
            sessionStorage.setItem(NARROW_NOTICE_KEY, '1');
        },
    },
};
</script>

<style scoped>
/* 設計 token 見 support/關卡編輯/README.md。卡片刻意不用陰影，一律 1px 細邊框。 */
.qe-page {
    /* ⚠ 這裡原本是 min-width: 1180px，但側欄佔掉 212px 之後內容區只有約 1150px，
       被強制撐寬就會被裁掉，而且外層沒有 overflow-x、連捲軸都沒有。
       改成不設下限：下面 .work 的 `260px + minmax(560px)` 已經定出約 838px 的
       自然最小寬度，一般螢幕都放得下。 */
    padding-bottom: 50px;
}

/* 這一頁所有卡片級容器的**唯一**內距來源。
   ⚠ 之前每一區各自宣告（題目卡 16/20/18、畫格清單 12/12/14、編輯區 18/22/24、
   模式設定區 16/18），四種值看起來就是亂的。要例外時請在該區寫明理由，
   不要默默改一個相近的數字。 */
.card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 18px 20px;
}

.qe-bar {
    display: flex;
    align-items: center;
    margin-bottom: 14px;
}

.qe-back {
    font-size: 15px;
    font-weight: 500;
    color: #1976d2;
    white-space: nowrap;
    text-decoration: none;
}

.qe-spacer {
    flex: 1;
}

.qe-save {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.qe-save-hint {
    color: #b71c1c;
    font-size: 12px;
}

.qe-unsaved {
    font-size: 13px;
    color: #757575;
}

.chk-wrap {
    position: relative;
}

.chk {
    border-radius: 16px;
    padding: 9px 14px;
    font-size: 13px;
    border: none;
    cursor: pointer;
}

.chk--ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.chk--warn {
    background: #fff3e0;
    color: #e65100;
}

.chk--block {
    background: #ffebee;
    color: #c62828;
}

.chk-pop {
    position: absolute;
    top: 46px;
    right: 0;
    z-index: 6;
    width: 340px;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.22);
    padding: 16px 18px 12px;
    text-align: left;
}

.chk-pop-head {
    font-size: 13px;
    color: #757575;
    margin-bottom: 8px;
}

.chk-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 7px 6px;
    border-radius: 4px;
    width: 100%;
}

.chk-row--btn {
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
}

.chk-row--btn:hover {
    background: #f5f5f5;
}

.chk-mark {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
}

.chk-mark--ok {
    background: #e8f5e9;
    color: #2e7d32;
}

.chk-mark--warn {
    background: #fff3e0;
    color: #e65100;
}

.chk-mark--block {
    background: #ffebee;
    color: #c62828;
}

.chk-body {
    min-width: 0;
}

.chk-label {
    display: block;
    font-size: 14px;
    color: #212121;
}

.chk-reason {
    display: block;
    font-size: 12px;
    color: #e65100;
    line-height: 1.5;
}

.chk-reason--block {
    color: #c62828;
}

.chk-pop-foot {
    font-size: 12px;
    color: #757575;
    border-top: 1px solid #f0f0f0;
    padding-top: 8px;
    margin-top: 6px;
}

.qe-question {
    margin-bottom: 16px;
}

/* ⚠ **grid 而不是 flex-wrap**（2026-08-31 依設計圖）。
   flex-wrap 的問題是欄位在變窄的過程中會先折成「兩欄 + 一欄」再堆疊，中間那段很醜；
   grid 是在斷點直接切成一欄，沒有中間狀態。
   ⚠ **預設兩欄**：這個 class 有兩個使用者——題目卡（三欄）與畫格編輯器（兩欄）。
   寫死三欄會讓畫格編輯器右邊空出一個 260px 的空格。 */
.two-col {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    /* 24px（設計圖的值）在實機上把欄位推得太開，文字欄反而不夠寬 */
    gap: 16px;
    align-items: start;
}

/* 題目卡多一個固定寬度的封面欄。minmax(0,1fr) 的 0 是為了讓長字串能收縮，
   而不是把格子撐開。 */
.two-col--quest {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 260px;
}

/* ⚠ **class 名稱不能叫 `col`。** Vuetify 的格線系統有全域的 `.col { padding: 12px }`，
   撞上去之後每一欄都多 12px 內距——欄與欄之間變成 12 + gap + 12，看起來像卡片有兩層
   padding，而 scoped style 裡完全找不到來源（2026-08-31 實際踩到）。 */
.qe-col {
    min-width: 0;
}

.lbl {
    font-size: 12px;
    color: #757575;
    margin-bottom: 6px;
}

/* 學生端會顯示這一句，老師要知道 */
.lbl-note {
    color: #9e9e9e;
}

.thumb-row {
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.thumb-wrap {
    position: relative;
    flex: 0 0 86px;
    line-height: 0;
}

.thumb {
    width: 86px;
    height: 60px;
    /* 學生端是 16:9 裁切顯示，這裡用同樣的 object-fit 讓老師看到一致的結果 */
    object-fit: cover;
    border-radius: 3px;
    background: #f0f0f0;
}

.thumb-side {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
}

.cnt--left {
    text-align: left;
    margin-top: 0;
}

/* 移除封面後墊底的是舊 thumbnail_url 而不是預設圖（規格 §4.3）——這個提示要看得見 */
.cover-legacy {
    font-size: 11px;
    color: #ef6c00;
    line-height: 1.5;
    margin-top: 4px;
}

.btn-text-danger {
    font-size: 12px;
    color: #d32f2f;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}

.btn-text-danger:disabled {
    color: #bdbdbd;
    cursor: not-allowed;
}

.in {
    width: 100%;
    padding: 11px 13px;
    border: 1px solid #bdbdbd;
    border-radius: 4px;
    outline: none;
    font-size: 15px;
    font-family: inherit;
    color: #212121;
    background: #fff;
}

.in:focus {
    border-color: #1e88e5;
}

.in--lg {
    font-size: 16px;
}

.ta {
    height: 44px;
    resize: vertical;
    line-height: 1.5;
}

/* 設計圖的高度。⚠ 這一列以前是被右邊的縮圖欄撐開的，改用 MediaSlot 之後縮圖欄變矮，
   所以改由這個高度決定整列高度。
   說明框只有 44px 會在旁邊留一塊空白。拉高到跟那一欄差不多，順便多一行可寫。 */
.ta--tall {
    height: 116px;
}

.cnt {
    text-align: right;
    font-size: 12px;
    color: #9e9e9e;
    margin-top: 4px;
}

/* 內嵌計數的定位基準 */
.field {
    position: relative;
}

/* ⚠ 只給題目卡的標題／說明用。畫格編輯器的兩處計數（`.cnt`）維持框外，
   那是這次刻意不動的範圍。 */
.cnt--inset {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    margin-top: 0;
    /* 不要擋住輸入框的點擊與游標定位 */
    pointer-events: none;
}

/* textarea 版：貼底而不是垂直居中，並加底色蓋住捲到下面的文字 */
.cnt--bottom {
    top: auto;
    bottom: 9px;
    right: 12px;
    transform: none;
    background: #fff;
    padding-left: 6px;
}

/* 給計數讓出空間。⚠ 用獨立的 class 而不是改 `.in` / `.in--lg`——
   那兩個是共用的（畫格編輯器 L277／L285 也在用），改全域會讓那邊也留一塊白。 */
.in--counted {
    padding-right: 62px;
}

.ta--counted {
    padding-bottom: 26px;
}

/* 主工作區：左 260 / 右 minmax(560,1fr)；編輯區跨兩列，學生端示意 sticky */
.work {
    display: grid;
    grid-template-columns: 260px minmax(560px, 1fr);
    gap: 16px 18px;
    align-items: start;
    /* 真的放不下時可以左右滑動，不要再靜默裁掉。
       ⚠ 代價：這讓 .work 成為捲動容器，所以「學生端示意」的 sticky 會失效
       （見 .preview 的註解）。內容不被裁掉比預覽跟著捲更重要。 */
    overflow-x: auto;
}

.rail {
    grid-column: 1;
    grid-row: 1;
}

.preview {
    grid-column: 1;
    grid-row: 2;
    /* 原本是 position: sticky; top: 74px。父層 .work 加了 overflow-x: auto 之後
       overflow-y 會被推導成 auto，.work 變成捲動容器、而它沒有固定高度，
       sticky 就永遠不會生效。與其留一段沒作用的樣式，直接拿掉。 */
}

.editor {
    grid-column: 2;
    grid-row: 1 / span 2;
}

.rail-head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rail-title {
    font-size: 14px;
    font-weight: 700;
    color: #212121;
}

.rail-count {
    font-size: 12px;
    color: #9e9e9e;
}

.rail-head .rail-count {
    margin-left: auto;
}

.rail-hint {
    font-size: 12px;
    color: #9e9e9e;
    margin: 2px 0 10px;
}

.rail-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.pcard {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 11px 12px 12px;
    background: #fff;
    cursor: pointer;
}

.pcard:hover {
    border-color: #90caf9;
}

/* 選取用 2px 藍框，內距同步減 1px 才不會跳動 */
.pcard--on {
    border: 2px solid #1e88e5;
    padding: 10px 11px 11px;
}

.pcard-top {
    display: flex;
    align-items: center;
    gap: 6px;
}

.pcard-no {
    font-size: 12px;
    color: #9e9e9e;
}

/* 橘點只在有問題時出現；完成的格子不顯示任何點（刻意降噪） */
.dot-warn {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffa726;
}

.mv {
    width: 20px;
    height: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 3px;
    background: #fff;
    color: #757575;
    font-size: 11px;
    line-height: 1;
    cursor: pointer;
}

.mv:disabled {
    border-color: #f0f0f0;
    color: #e0e0e0;
    cursor: not-allowed;
}

.pcard-title {
    font-size: 14px;
    color: #212121;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pcard-title.is-empty {
    color: #bdbdbd;
}

.pcard-type,
.pcard-sum {
    font-size: 11px;
    color: #9e9e9e;
}

.pcard-sum {
    color: #bdbdbd;
}

.add-panel {
    display: block;
    width: 100%;
    margin-top: 8px;
    border: 1px dashed #bdbdbd;
    border-radius: 6px;
    background: #fafafa;
    color: #616161;
    font-size: 13px;
    padding: 12px;
    text-align: center;
    cursor: pointer;
}

.add-panel:hover {
    border-color: #1e88e5;
    color: #1e88e5;
    background: #f3f8fe;
}

.add-panel--full,
.add-panel--full:hover {
    border-color: #e0e0e0;
    background: #fafafa;
    color: #9e9e9e;
    font-size: 12px;
    cursor: default;
}

.add-panel-hint {
    font-size: 12px;
}

.ed-empty {
    font-size: 13px;
    color: #9e9e9e;
    padding: 40px 0;
    text-align: center;
}

.ed-type-help {
    color: #607d8b;
    margin-left: -6px;
}

.ed-type-info {
    padding: 16px 18px;
    background: #fff;
    color: #455a64;
    font-size: 14px;
    line-height: 1.7;
    white-space: normal;
    overflow-wrap: anywhere;
    max-height: 65vh;
    overflow-y: auto;
}

.ed-type-info-hint {
    color: #607d8b;
    font-size: 12px;
    margin-bottom: 12px;
}

.ed-type-info-current {
    padding-bottom: 12px;
}

.ed-type-detail {
    border-top: 1px solid #eceff1;
    padding: 10px 0;
}

.ed-type-detail summary {
    cursor: pointer;
    color: #263238;
    font-weight: 500;
}

.ed-type-detail > div {
    margin-top: 8px;
}

.ed-type-info-title {
    color: #263238;
    font-weight: 600;
    margin-bottom: 6px;
}

.ed-head {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ed-no {
    font-size: 17px;
    font-weight: 500;
    color: #212121;
}

.ed-type {
    font-size: 13px;
    color: #757575;
}

.btn-danger {
    border: 1px solid #ef9a9a;
    color: #d32f2f;
    background: #fff;
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
}

.btn-danger:disabled {
    border-color: #e0e0e0;
    color: #bdbdbd;
    cursor: not-allowed;
}

.ed-desc {
    font-size: 13px;
    color: #757575;
    line-height: 1.7;
    margin-top: 6px;
}

.ed-section {
    border-top: 1px solid #f0f0f0;
    padding-top: 18px;
    margin-top: 18px;
}

.sec-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
}

.sec-title {
    font-size: 14px;
    font-weight: 500;
    color: #212121;
}

.sec-desc {
    font-size: 13px;
    color: #757575;
    line-height: 1.7;
    margin-bottom: 12px;
}

/* 兩行相連的說明，只有最後一行留下方間距 */
.sec-desc--tight {
    margin-bottom: 2px;
}

.act-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

/* 虛線＝還沒有範例、實線＝已有；2px 藍＝目前選中 */
.act {
    font-size: 13px;
    border-radius: 14px;
    padding: 7px 12px;
    background: #fff;
    border: 1px dashed #bdbdbd;
    color: #9e9e9e;
    cursor: pointer;
}

.act--has {
    border: 1px solid #90a4ae;
    color: #37474f;
}

.act--on {
    border: 2px solid #1e88e5;
    color: #1565c0;
    padding: 6px 11px;
}

.btn-outline {
    border: 1px solid #1e88e5;
    color: #1e88e5;
    background: #fff;
    border-radius: 4px;
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
}

/* ≤1179：改成堆疊，DOM 順序即顯示順序（畫格清單 → 學生端示意 → 編輯區） */
@media (max-width: 1179px) {
    [data-rwd="work"] {
        display: block;
        /* 堆疊後每一塊都是全寬，不會溢出，橫向捲動就不需要了 */
        overflow-x: visible;
    }

    [data-rwd="rail"],
    [data-rwd="pv"] {
        margin-bottom: 16px;
    }

    [data-rwd="pv"] {
        max-width: 420px;
    }

    /* 窄螢幕時畫格清單改成橫向捲動，不佔掉整個高度 */
    [data-rwd="raillist"] {
        flex-direction: row;
        overflow-x: auto;
    }

    [data-rwd="raillist"] > .pcard {
        flex: 0 0 216px;
    }

}

/* ⚠ 題目卡的斷點與主工作區**分開**（2026-08-31 依設計圖：`w < 1100` 才堆疊）。
   主工作區在 1179 就換窄版，題目卡撐到 1100——三欄需要的寬度比主工作區少。
   改成 grid 之後不必再重設 flex：只要換 grid-template-columns 就是單欄。 */
@media (max-width: 1099px) {
    /* ⚠ 要蓋過 .two-col--quest 的三欄宣告，所以選擇器要夠具體 */
    [data-rwd="two"],
    [data-rwd="two"].two-col--quest {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
    }

    /* 單欄時說明欄不需要固定高度，讓它跟著內容 */
    [data-rwd="two"] .ta--tall {
        height: auto;
        min-height: 88px;
    }
}

/* <768 不專門支援，但也不封鎖——只讓標題列與浮層不要溢出視窗 */
@media (max-width: 767px) {
    .qe-bar {
        flex-wrap: wrap;
        gap: 8px;
    }

    /* 只換掉標題列那一顆——.qe-spacer 在畫格卡與區塊標題也有用到 */
    .qe-bar > .qe-spacer {
        flex-basis: 100%;
    }

    .chk-pop {
        width: min(340px, calc(100vw - 48px));
    }

    [data-rwd="pv"] {
        max-width: none;
    }
}
</style>
